import NextLink from "next/link"
import {
  Box,
  Input,
  FormLabel,
  Button,
  Center,
  Link,
  Heading,
  FormControl,
  useToast,
  FormErrorMessage,
} from "@chakra-ui/react"
import { useForm } from "react-hook-form"
import { useTranslations } from "use-intl"
import { PageSEO } from "@components/HeadSEO"
import supabase from "@utils/initSupabase"

const SignIn = (): JSX.Element => {
  const { register, formState, handleSubmit } = useForm()
  const toast = useToast()
  const t = useTranslations()

  const handleSignIn = async ({
    email,
    password,
  }: {
    email?: string
    password?: string
  }) => {
    const { error } = await supabase.auth.signIn({
      email,
      password,
    })

    if (error) {
      toast({
        title: t("signin.toastErrorTitle"),
        description: t("signin.toastErrorMsg"),
        status: "error",
        duration: 9000,
        isClosable: true,
      })
    } else {
      toast({
        title: t("signin.toastSuccessTitle"),
        description: t("signin.toastSuccessMsg"),
        status: "success",
        duration: 5000,
        isClosable: true,
      })
    }
  }

  return (
    <>
      <PageSEO
        title={String(t("signin.pageTitle"))}
        description={String(t("signin.pageDescription"))}
      />
      <Center>
        <Box
          maxW="30rem"
          w="100%"
          mt={[6, null, 12, 16, 20]}
          p="6"
          borderRadius="lg"
          overflow="hidden"
        >
          <Heading variant="contentHeading1">
            {t("signin.signInHeading")}
          </Heading>
          <form onSubmit={handleSubmit(handleSignIn)}>
            <FormControl isInvalid={!!formState.errors.email}>
              <FormLabel htmlFor="email" mt="4">
                {t("signin.formEmailLabel")}
              </FormLabel>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                placeholder={String(t("signin.formEmailPlaceholder"))}
                focusBorderColor="orange"
                {...register("email", {
                  pattern: {
                    value: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/, // eslint-disable-line
                    message: String(t("signin.formEmailErrorMsg")),
                  },
                })}
              />
              <FormErrorMessage color="white">
                {typeof formState.errors.email?.message === "string"
                  ? formState.errors.email.message
                  : null}
              </FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="password" mt="4">
                {t("signin.formPasswordLabel")}
              </FormLabel>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder={String(t("signin.formPasswordPlaceholder"))}
                focusBorderColor="orange"
                {...register("password")}
              />
            </FormControl>

            <Button
              type="submit"
              size="md"
              w="100%"
              mt="8"
              disabled={formState.isSubmitting}
            >
              {formState.isSubmitting
                ? t("signin.submitLoading")
                : t("signin.submit")}
            </Button>
            <NextLink href={String(t("navigation.signUpUrl"))} passHref>
              <Link display="block" textAlign="center" mt="5">
                {t("signin.signUpMsg")}
              </Link>
            </NextLink>

            <NextLink href={String(t("signin.resetUrl"))} passHref>
              <Link display="block" textAlign="center" mt="4">
                {t("signin.resetMsg")}
              </Link>
            </NextLink>
          </form>
        </Box>
      </Center>
    </>
  )
}

export default SignIn
